import { getClientAlias } from 'util/client-alias';
import { getClientId } from 'util/client-id';
import { incApi } from '../inc-api';
import {
  ExportGuestJourneyDetailsCustomersRequest,
  GuestJourneyCustomerExportResponse,
  GuestJourneyFilters,
  GuestJourneyCardsParams,
  GuestJourneyDetailsCards,
  GuestJourneyDetailsResponse,
  GuestJourneyDistributions,
  GuestJourneyStageGrid,
  GuestJourneyMemberTrendsResponse,
  GuestJourneyOffer,
  GuestJourneyOfferImpactCards,
  GuestJourneyPopulationCards,
  GuestJourneyPopulationParams,
  GuestJourneyRetentionCards,
  GuestJourneyRetentionParams,
  GuestJourneyRetentionVisit,
  GuestJourneyBenchmarksKpiCardDTO,
  GuestJourneyBenchmarksOverviewResponse,
  GuestJourneyBenchmarksMetricsResponse,
  GuestJourneyBenchmarkSection,
  GuestJourneyBenchmarkTimeframe,
  GuestJourneyPopulation,
  GuestJourneySankeyResponse,
  GuestJourneyStageMovementCards,
  GuestJourneyStageMovementDataResponse,
  GuestJourneyVioTrend,
  GuestJourneyVioTrendsResponse,
} from './guest-journey-reports.types';

/**
 * Card endpoints live under the BE's `/v2/guest-journey` base and take the
 * wire param names (`storeIds`, `offerIds`, `population`, …) — use the
 * wrapped hooks in `guest-journey-hooks.ts`, which transform the page search
 * params, rather than calling these hooks directly from components.
 */

/**
 * Benchmarks endpoints take `year` (int) + `benchmarkCuisine`; the page carries
 * the year as the `benchmarkYear` search-param (string). Rename it to the wire
 * `year` (dropped when absent) and pass the rest of the filter params through.
 */
const toBenchmarkParams = ({
  benchmarkYear,
  ...rest
}: GuestJourneyFilters) => ({
  ...rest,
  ...(benchmarkYear ? { year: benchmarkYear } : {}),
});

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    // Per-population summary cards (`oneOf` Active|Sliding|Churn) — the BE
    // discriminates by the `{population}` path segment. The per-population
    // wrapped hooks in `guest-journey-hooks.ts` inject the segment + filters.
    getGuestJourneyPopulationSummary: build.query<
      GuestJourneyPopulationCards,
      GuestJourneyCardsParams
    >({
      query: ({ population, ...params }) => ({
        url: `incentivio-reports/v2/guest-journey/${population}/summary`,
        method: 'GET',
        params,
      }),
    }),

    // Per-population segment grid (Count / Spend / Visits). Tier axis labels
    // bind from `/settings`, not from this response.
    getGuestJourneyPopulationOverview: build.query<
      GuestJourneyStageGrid,
      GuestJourneyPopulationParams
    >({
      query: ({ population, ...params }) => ({
        url: `incentivio-reports/v2/guest-journey/${population}/overview`,
        method: 'GET',
        params,
      }),
    }),

    // Per-population Member Count Trends time series.
    getGuestJourneyPopulationTrends: build.query<
      GuestJourneyMemberTrendsResponse,
      GuestJourneyPopulationParams
    >({
      query: ({ population, ...params }) => ({
        url: `incentivio-reports/v2/guest-journey/${population}/trends`,
        method: 'GET',
        params,
      }),
    }),

    // Per-population "Vio's Top Trends" insights. The BE returns a bare
    // `GuestJourneyInsightDTO[]`; wrap it so consumers read `data.trends` (and
    // the `useSearchQuery` empty-`{}` default stays safe). The per-population
    // wrapped hooks below inject the `{population}` path segment.
    getGuestJourneyPopulationInsights: build.query<
      GuestJourneyVioTrendsResponse,
      GuestJourneyPopulationParams
    >({
      query: ({ population, ...params }) => ({
        url: `incentivio-reports/v2/guest-journey/${population}/insights`,
        method: 'GET',
        params,
      }),
      transformResponse: (trends: GuestJourneyVioTrend[]) => ({ trends }),
    }),

    // Active distributions — one payload for the spend donut, visits donut, and
    // acquisition-source bars (folds the former three `*-donut`/`first-source`
    // endpoints).
    getGuestJourneyActiveDistributions: build.query<
      GuestJourneyDistributions,
      GuestJourneyFilters
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/active/distributions',
        method: 'GET',
        params,
      }),
    }),

    // GJ-scoped offer list for the Offer Impact filter — only offers with
    // impact data, each with its offer date.
    getGuestJourneyOffers: build.query<
      GuestJourneyOffer[],
      GuestJourneyFilters
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/offers',
        method: 'GET',
        params,
      }),
    }),

    getGuestJourneyDetailsCards: build.query<
      GuestJourneyDetailsCards,
      GuestJourneyCardsParams
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/details/summary',
        method: 'GET',
        params,
      }),
    }),

    getGuestJourneyDetails: build.query<
      GuestJourneyDetailsResponse,
      GuestJourneyFilters
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/details/data',
        method: 'GET',
        params,
      }),
    }),

    getGuestJourneyStageMovementCards: build.query<
      GuestJourneyStageMovementCards,
      GuestJourneyCardsParams
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/stage-movement/summary',
        method: 'GET',
        params,
      }),
    }),

    // Single movement payload per tab — the same flows feed the sankey and
    // the incoming/outgoing tables. The stage variant arrives wrapped in a
    // `{ sankey }` envelope (BE `GuestJourneyStageMovementResponseDTO`);
    // unwrap it so both tabs consume the same `GuestJourneySankeyResponse`.
    getGuestJourneyStageMovementData: build.query<
      GuestJourneySankeyResponse,
      GuestJourneyCardsParams
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/stage-movement/data',
        method: 'GET',
        params,
      }),
      transformResponse: (response: GuestJourneyStageMovementDataResponse) =>
        response.sankey,
    }),

    getGuestJourneyOfferImpactCards: build.query<
      GuestJourneyOfferImpactCards,
      GuestJourneyCardsParams
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/offer-impact/summary',
        method: 'GET',
        params,
      }),
    }),

    getGuestJourneyOfferImpactSankey: build.query<
      GuestJourneySankeyResponse,
      GuestJourneyCardsParams
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/offer-impact/sankey',
        method: 'GET',
        params,
      }),
    }),

    getGuestJourneyRetentionCards: build.query<
      GuestJourneyRetentionCards,
      GuestJourneyRetentionParams
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/retention/summary',
        method: 'GET',
        params,
      }),
    }),

    getGuestJourneyRetentionConversionTrends: build.query<
      GuestJourneyRetentionVisit[],
      GuestJourneyRetentionParams
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/retention/conversion-trends',
        method: 'GET',
        params,
      }),
      // The BE sends `conversionFromLastVisitPct` as an integer percent (e.g.
      // 86); the FE works in 0–1 fractions everywhere downstream (§2).
      transformResponse: (visits: GuestJourneyRetentionVisit[]) =>
        Array.isArray(visits)
          ? visits.map(visit => ({
              ...visit,
              // Preserve null (no data) — dividing would coerce it to 0.
              conversionFromLastVisitPct:
                visit.conversionFromLastVisitPct == null
                  ? null
                  : visit.conversionFromLastVisitPct / 100,
            }))
          : [],
    }),

    // Benchmarks tab (spec v0.5) is two endpoints, both taking the tab's filter
    // params. `benchmarkYear` (page search-param, string) maps to the wire
    // `year`; `benchmarkCuisine` passes through. See `toBenchmarkParams`.
    getBenchmarksOverview: build.query<
      GuestJourneyBenchmarksOverviewResponse,
      GuestJourneyFilters
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/benchmarks/overview',
        method: 'GET',
        params: toBenchmarkParams(params),
      }),
      // BE returns a bare `GuestJourneyBenchmarksKpiCardDTO[]`; map each card to
      // the shared `GuestJourneyKpiCard` and wrap as `{ cards }` (§1 field map).
      transformResponse: (cards: GuestJourneyBenchmarksKpiCardDTO[]) => ({
        cards: Array.isArray(cards)
          ? cards.map(card => ({
              title: card.eyebrow,
              value: card.value,
              valueDescription:
                card.baselineLabel && card.baselineValue
                  ? `${card.baselineLabel} ${card.baselineValue}`
                  : card.baselineLabel ?? undefined,
              trend: card.delta ?? undefined,
              footnote: card.description,
            }))
          : [],
      }),
    }),

    getBenchmarksMetrics: build.query<
      GuestJourneyBenchmarksMetricsResponse,
      GuestJourneyFilters
    >({
      query: params => ({
        url: 'incentivio-reports/v2/guest-journey/benchmarks/metrics',
        method: 'GET',
        params: toBenchmarkParams(params),
      }),
      // BE returns a bare `GuestJourneyBenchmarkSection[]`; wrap as `{ sections }`.
      transformResponse: (sections: GuestJourneyBenchmarkSection[]) => ({
        sections: Array.isArray(sections) ? sections : [],
      }),
    }),

    // Timeframe options for the Benchmarks filter — the BE-owned set of
    // reporting timeframes to compare against (Year to Date, First Half, Last
    // Year), as display-ready {id,label} pairs. Fetched up front by the
    // Benchmarks bootstrap (no asyncOptions); the first entry is the default
    // selection. The selected `id` is sent verbatim as the `year` param.
    getBenchmarkTimeframes: build.query<GuestJourneyBenchmarkTimeframe[], void>(
      {
        query: () => ({
          url: 'incentivio-reports/v2/guest-journey/benchmarks/timeframes',
          method: 'GET',
        }),
      },
    ),

    // Details per-segment customer-list export (sc-357751 QA · activity 368127).
    // Emailed-CSV export of the specific customers in one spend×frequency
    // segment, mirroring the `exportCustomersCSV` envelope. `segment` is the
    // machine `SPEND_TIER_x_VISIT_TIER_y` key from the Details row.
    // ⚠️ Presumed endpoint — not yet built by the reports service (§D6).
    exportGuestJourneyDetailsCustomers: build.mutation<
      GuestJourneyCustomerExportResponse,
      ExportGuestJourneyDetailsCustomersRequest
    >({
      query: ({ segment, email, params }) => ({
        url: 'incentivio-reports/v2/guest-journey/details/customers/export',
        method: 'POST',
        body: {
          clientId: getClientId(),
          clientName: getClientAlias(),
          emailIds: [email],
          segment,
          ...params,
        },
      }),
    }),
  }),
});

// sc-374376: the Guest Journey report renders settings-driven stage labels, so a
// settings change (renamed stages / new thresholds) must refetch it — otherwise
// the cached response keeps the old names. Tag every report query under one key
// so `updateGuestJourneySettings` / `resetGuestJourneySettings` invalidate them
// together. (enhanceEndpoints mutates the definitions in place.)
endpoints.enhanceEndpoints({
  endpoints: {
    getGuestJourneyPopulationSummary: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyPopulationOverview: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyPopulationTrends: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyPopulationInsights: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyActiveDistributions: {
      providesTags: ['GuestJourneyReport'],
    },
    getGuestJourneyOffers: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyDetailsCards: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyDetails: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyStageMovementCards: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyStageMovementData: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyOfferImpactCards: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyOfferImpactSankey: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyRetentionCards: { providesTags: ['GuestJourneyReport'] },
    getGuestJourneyRetentionConversionTrends: {
      providesTags: ['GuestJourneyReport'],
    },
    getBenchmarksOverview: { providesTags: ['GuestJourneyReport'] },
    getBenchmarksMetrics: { providesTags: ['GuestJourneyReport'] },
    getBenchmarkTimeframes: { providesTags: ['GuestJourneyReport'] },
  },
});

export const {
  useGetGuestJourneyPopulationSummaryQuery,
  useGetGuestJourneyPopulationOverviewQuery,
  useGetGuestJourneyPopulationTrendsQuery,
  useGetGuestJourneyPopulationInsightsQuery,
  useGetGuestJourneyActiveDistributionsQuery,
  useGetGuestJourneyOffersQuery,
  useLazyGetGuestJourneyOffersQuery,
  useGetGuestJourneyDetailsCardsQuery,
  useGetGuestJourneyDetailsQuery,
  useGetGuestJourneyStageMovementCardsQuery,
  useGetGuestJourneyStageMovementDataQuery,
  useGetGuestJourneyOfferImpactCardsQuery,
  useGetGuestJourneyOfferImpactSankeyQuery,
  useGetGuestJourneyRetentionCardsQuery,
  useGetGuestJourneyRetentionConversionTrendsQuery,
  useGetBenchmarksOverviewQuery,
  useGetBenchmarksMetricsQuery,
  useGetBenchmarkTimeframesQuery,
  useExportGuestJourneyDetailsCustomersMutation,
} = endpoints;

/**
 * Per-population wrapper hooks for the `{population}/overview` and
 * `{population}/insights` endpoints: each injects its population path segment so
 * a tab keeps a stable, population-specific hook. Typed loosely (`any`) like
 * the card wrappers in `guest-journey-hooks.ts` so they satisfy
 * `useSearchQuery`'s `UseQuery` parameter, and they forward the query options
 * (skip token, etc.) the search layer passes as the second argument.
 */
const withPopulation =
  (useQuery: any, population: GuestJourneyPopulation) =>
  (params: GuestJourneyFilters = {}, options?: object) =>
    useQuery({ ...params, population }, options);

/**
 * The `{population}/trends` variant of `withPopulation`. The Member Count Trends
 * chart owns its own date picker (`trendsStart`/`trendsEnd`); the endpoint takes
 * the toolbar `dateRange`, so map those chart-scoped dates to the BE
 * `startdate`/`enddate` fields (bound server-side into `PulseDateRange`).
 */
const withPopulationTrends =
  (useQuery: any, population: GuestJourneyPopulation) =>
  (
    { trendsStart, trendsEnd, ...params }: GuestJourneyFilters = {},
    options?: object,
  ) =>
    useQuery(
      {
        ...params,
        population,
        ...(trendsStart ? { startdate: trendsStart } : {}),
        ...(trendsEnd ? { enddate: trendsEnd } : {}),
      },
      options,
    );

export const useGetGuestJourneyActiveGridQuery = withPopulation(
  useGetGuestJourneyPopulationOverviewQuery,
  'active',
);
export const useGetGuestJourneySlidersGridQuery = withPopulation(
  useGetGuestJourneyPopulationOverviewQuery,
  'sliding',
);
export const useGetGuestJourneyChurnedGridQuery = withPopulation(
  useGetGuestJourneyPopulationOverviewQuery,
  'churn',
);
export const useGetGuestJourneyActiveMemberTrendsQuery = withPopulationTrends(
  useGetGuestJourneyPopulationTrendsQuery,
  'active',
);
export const useGetGuestJourneySlidersMemberTrendsQuery = withPopulationTrends(
  useGetGuestJourneyPopulationTrendsQuery,
  'sliding',
);
export const useGetGuestJourneyChurnedMemberTrendsQuery = withPopulationTrends(
  useGetGuestJourneyPopulationTrendsQuery,
  'churn',
);

// "Vio's Top Trends" insights — one BE endpoint keyed by the `{population}` path
// segment; each wrapper injects its population so consuming tabs keep a stable
// hook (the old per-population vio-trends endpoints folded into this one).
export const useGetGuestJourneyActiveTrendsQuery = withPopulation(
  useGetGuestJourneyPopulationInsightsQuery,
  'active',
);
export const useGetGuestJourneySlidersTrendsQuery = withPopulation(
  useGetGuestJourneyPopulationInsightsQuery,
  'sliding',
);
export const useGetGuestJourneyChurnedTrendsQuery = withPopulation(
  useGetGuestJourneyPopulationInsightsQuery,
  'churn',
);
